import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  input,
  output,
  OnDestroy,
  signal,
  effect
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { MessageEx } from '../../../classes/messageex';
import { ChatCloudLinkService } from '../../../services/chat-cloudlink.service';
import { ChatConversationService } from '../../../services/chat-conversation.service';
import { ChatLoggingService } from '../../../services/chat-logging.service';
import { AudioHelpers } from './../../../classes/audiohelpers';
import type { AudioObj } from './../../chat/chat.component';
import { RecordService } from '../../../services/record.service';
import { PrimeThemeService } from '../../../themes/prime-theme.service';

@Component({
  selector: 'app-audio-card',
  templateUrl: './audio-card.component.html',
  styleUrls: ['./audio-card.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AudioCardComponent implements OnDestroy {
  isVoicemail = input<boolean>();
  item = input<MessageEx>();
  attachmentName = input('', { alias: 'attachmentName' });
  attachmentSize = input('', { alias: 'attachmentSize' });
  currentTabIndex = input('', { alias: 'currentTabIndex' });
  blobUrl = input('', { alias: 'blobUrl' });
  isOffline = input(false, { alias: 'isOffline' });
  isMobile = input(false, { alias: 'isMobile' });
  previewMode = input(false);

  // Private property for blobUrl updates since it's both input and needs to be modified
  private _blobUrl: string;

  @ViewChild('audio') audio: ElementRef;
  @ViewChild('waveformsCanvas') waveformsCanvas: ElementRef;
  @ViewChild('progressCanvas') progressCanvas: ElementRef;

  onDownloadAttachmentClicked = output<string>();

  // Computed signals for derived values
  conversationId = computed<string>(() => {
    return this.item()?.conversationId ?? '';
  });

  attachmentId = computed<string>(() => {
    return this.item()?.body ?? '';
  });

  audioLoaded = signal<boolean>(false);
  public audioContext: AudioContext;
  private resizeTimer;
  audioObj = signal<AudioObj>({
    audioPlaying: false,
    audioStart: '',
    audioProgress: '0%',
    audioEnd: ''
  });

  record = signal<any>(null);

  // Signal to track when view is initialized and canvas elements are ready
  private viewInitialized = signal<boolean>(false);

  // Convert observable to signal for reactive effects
  private currentlyPlayingSignal = toSignal(this.recordService.currentlyPlaying$, { initialValue: null });

  constructor(
    private cdRef: ChangeDetectorRef,
    public chatCloudLinkService: ChatCloudLinkService,
    public translateService: TranslateService,
    private chatConversationService: ChatConversationService,
    private chatLoggingService: ChatLoggingService,
    private recordService: RecordService,
    public primeThemeService: PrimeThemeService
  ) {
    // Effect 1: Manage record state reactively based on voicemail status
    effect(() => {
      if (this.isVoicemail() && this.item()?.clCompChat_displayBody) {
        try {
          this.record.set(JSON.parse(this.item().clCompChat_displayBody)?.record);
        } catch (error) {
          this.chatLoggingService.messageError({
            exceptionLogEvent: {
              logMessage: 'audio-card.component: Error parsing record',
              logParameters: [error]
            },
            errorMessage: 'Failed to parse voicemail record'
          });
          this.record.set(null);
        }
      } else {
        this.record.set(null);
      }
    });

    // Effect 2: Handle external audio state changes reactively
    effect(() => {
      const currentlyPlayingId = this.currentlyPlayingSignal();

      if (currentlyPlayingId && this.audioObj().audioPlaying && this.item()?.messageId !== currentlyPlayingId) {
        this.playAudioMessage(this.item().clCompChat_displayBody, !this.isVoicemail());
      }
    });
  }

  @HostListener('window:resize', [])
  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (this.audioLoaded()) {
        AudioHelpers.drawAudio(
          this.audio.nativeElement.src,
          this.audioContext,
          this.chatLoggingService,
          'audio-card',
          this.waveformsCanvas,
          this.progressCanvas,
          '#0073D0',
          this.attachmentId()
        );
      }
    }, 250);
  }

  ngAfterViewInit() {
    // Signal that view is ready and canvas elements are available
    this.viewInitialized.set(true);

    // Effects handle record state and external audio state management automatically
    // This method is now simplified since reactive effects replace the subscription logic
  }

  ngOnDestroy() {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }

    // Revoke any created object URL to avoid leaking memory
    const blobUrl = (this as any)._blobUrl as string | undefined;
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      (this as any)._blobUrl = undefined;
    }
    // Effects automatically clean up when component is destroyed
    // No manual subscription cleanup needed
  }

  public async playAudioMessage(audioId: string, isAttachment: boolean) {
    try {
      let audio: HTMLAudioElement = this.audio?.nativeElement;
      // Set up audio context
      var AudioContext =
        (<any>window).AudioContext || // Default
        (<any>window).webkitAudioContext || // Safari and old versions of Chrome
        false;
      this.audioContext = new AudioContext();
      if (this.audioObj().audioPlaying) {
        audio.pause();
        setTimeout(() => {
          this.audioObj.update(obj => ({ ...obj, audioPlaying: false }));
          this.cdRef.detectChanges();
        }, 100);
      } else {
        if (!this.audioLoaded()) {
          var response;
          if (isAttachment) {
            response = await this.downloadAudioAttachment(audioId);
          } else {
            if (audioId.indexOf('recordingId') > 0) {
              // this is a stringy object!! i.e. the action card gave us an object that we need to parse out the recording
              audioId = (JSON.parse(audioId) as any).record.recordingId;
            }
            // the sdk used is OLD and not inline with the odata params required.. media requires an update
            // we use serviceRequester to fix this and still maintain auth integrity of the micro goodness but skip the sdk magic
            const result = await (this.chatCloudLinkService.media as any).serviceRequester.request({
              method: 'GET',
              path: `/recordings/${audioId}`,
              query: {
                $expand: 'media' // this gives us the media link
              }
            });

            // and now our recording url is embeded in the body
            response = result.body._embedded.media.url;
          }
          audio.src = response;
          audio.load();
          this.audioLoaded.set(true);
        }
        audio.play().then(() => {
          this.audioObj.update(obj => ({ ...obj, audioPlaying: true }));
        });
        this.recordService.currentlyPlaying$.next(this.item()?.messageId);

        // Ensure audio visualization happens after everything is ready
        // Use a longer delay to ensure canvas elements and audio src are fully ready
        setTimeout(() => {
          if (this.viewInitialized() && this.waveformsCanvas && this.progressCanvas) {
            AudioHelpers.drawAudio(
              audio.src,
              this.audioContext,
              this.chatLoggingService,
              'audio-card',
              this.waveformsCanvas,
              this.progressCanvas,
              '#0073D0',
              this.attachmentId()
            );
          }
        }, 100);
        AudioHelpers.drawAudio(
          audio.src,
          this.audioContext,
          this.chatLoggingService,
          'audio-card',
          this.waveformsCanvas,
          this.progressCanvas,
          '#0073D0',
          this.attachmentId()
        );
      }
    } catch (ex) {
      this.chatLoggingService.messageError({
        exceptionLogEvent: {
          logMessage: 'audio-card.component.playAudioMessage(): Error',
          logParameters: [audioId, ex]
        },
        errorMessage: this.translateService.instant('cl_chat.failedToPlayAudio')
      });
    }
  }

  public audioUpdateTime() {
    const audio = this.audio?.nativeElement;
    if (!audio) return;

    let audioDuration = 0;
    if (audio.duration != Infinity && !isNaN(audio.duration)) {
      audioDuration = audio.duration;
    }

    const currentTime = audio.currentTime || 0;
    const progress = audioDuration ? (currentTime / audioDuration) * 100 : 0;
    let progressPercent = progress + '%';

    if (isNaN(progress)) {
      progressPercent = '0%';
    }

    // Format time using basic JS instead of moment to avoid dependencies
    const formatTime = (seconds: number) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const audioStart = formatTime(currentTime);
    const audioEnd = formatTime(audioDuration);

    // Get current playing state from signal and only change it if audio is finished
    const currentAudioObj = this.audioObj();
    const audioPlaying = progressPercent === '100%' ? false : currentAudioObj.audioPlaying;

    // Update signal with new values
    this.audioObj.set({
      audioPlaying,
      audioStart,
      audioProgress: progressPercent,
      audioEnd
    });

    // Trigger change detection for OnPush + signals compatibility
    this.cdRef.detectChanges();
  }

  private async downloadAudioAttachment(attachmentId: string) {
    return new Promise(async (resolve, reject) => {
      try {
        this.chatLoggingService.info({
          logMessage: 'audio-card.component.downloadAudioAttachment()',
          logParameters: [attachmentId]
        });
        var blob = await this.chatConversationService.getAttachmentAsBlob({
          conversationId: this.conversationId(),
          attachmentId: attachmentId
        });

        let reader = new FileReader();
        reader.onloadend = e => {
          let binary = AudioHelpers.convertDataURIToBinary(reader.result);
          let audioBlob = new Blob([binary], { type: 'audio/wav' });

          if (this.chatCloudLinkService.browser.name === 'ios-webview') {
            var typeRegex = new RegExp('data:application/.*?;');
            var match = (<string>reader.result).match(typeRegex);
            var audioData = <string>reader.result;
            if (match) {
              audioData = (<string>reader.result).replace(match[0], 'data:audio/wav;');
            } else if (audioData.indexOf('data:;base64') === 0) {
              audioData = audioData.replace('data:;base64', 'data:audio/wav;base64');
            }
            resolve(audioData);
          } else {
            this._blobUrl = URL.createObjectURL(audioBlob);
            resolve(this._blobUrl);
          }
        };
        reader.readAsDataURL(blob);
      } catch (ex) {
        reject(ex);
      }
    });
  }

  downloadAttachment() {
    this.onDownloadAttachmentClicked.emit(this.item().clCompChat_displayBody);
  }
}
